import request from 'superagent';
import { logSessionInfo } from '../services/logger';
import networkLogger from './networkLogger';

/**
 * GraphQL query to get the current period from the Calendar Service
 */
const GET_CURRENT_PERIOD = `
  query getCurrentPeriod($schoolId: String!) {
    getCurrentPeriod(schoolId: $schoolId) {
      periodIdentifier
      schoolYear
    }
  }`;

/**
 * GraphQL query to get the school year start and end dates from the Calendar Service
 */
const GET_CURRENT_SCHOOL_CALENDAR = `
  query getCurrentSchoolYearCalendar($districtId: String!, $schoolId: String!) {
    getCurrentSchoolYearCalendar(districtId: $districtId, schoolId: $schoolId) {
      startDate
      endDate
    }
  }`;

/**
 * GraphQL query to get the assessment calendar from the Calendar Service
 */
const GET_ASSESSMENT_CALENDAR = `
  query GetAssessmentCalendar($schoolId: String, $districtId: String) {
    assessmentCalendar(schoolId: $schoolId, districtId: $districtId) {
      schoolId
      schoolYear
      startDate
      periods {
        name
        periodIdentifier
        startDate
        endDate
        schoolYear
        tags
      }
    }
  }`;

/**
 * Makes a GraphQL request to the Calendar Service
 * 
 * @param {string} query - GraphQL query string
 * @param {Object} variables - Variables for the query
 * @returns {Promise<Object>} - Response data
 */
async function graphqlRequest(query, variables) {
  const startTime = Date.now();
  try {
    const response = await request.post(process.env.CALENDAR_SERVICE_URL)
      .timeout({ response: 10000, deadline: 15000 }) // 10s response timeout, 15s total deadline
      .set('Content-Type', 'application/json')
      .send({query: query, variables});
    const {body} = response;
    const duration = Date.now() - startTime;

    if (body.errors) {
      console.log(body.errors);
      networkLogger.logGraphQLError(
        'CalendarService',
        process.env.CALENDAR_SERVICE_URL,
        null,
        body.errors,
        duration
      );
      const graphqlError = new Error(body.errors[0].message);
      graphqlError.isGraphQLError = true;
      throw graphqlError;
    }

    return body.data;
  } catch (error) {
    if (!error.isGraphQLError) {
      networkLogger.logGraphQLError(
        'CalendarService',
        process.env.CALENDAR_SERVICE_URL,
        null,
        error,
        Date.now() - startTime
      );
    }
    throw error;
  }
}

/**
 * Gets calendar information for a school
 * 
 * @param {string} schoolId - The ID of the school
 * @returns {Promise<Object>} - Object containing schoolYear and periodIdentifier
 */
async function getCurrentPeriod(schoolId) {
  const response = await graphqlRequest(GET_CURRENT_PERIOD, {schoolId});

  if (!response.getCurrentPeriod) {
    return null;
  }

  return {
    schoolYear: response.getCurrentPeriod.schoolYear,
    periodIdentifier: response.getCurrentPeriod.periodIdentifier
  };
}

/**
 * Gets school year calendar information for a school
 * 
 * @param {string} districtId - The ID of the district
 * @param {string} schoolId - The ID of the school
 * @returns {Promise<Object>} - Object containing startDate and endDate
 */
async function getCurrentSchoolYearCalendar(districtId, schoolId) {
  const response = await graphqlRequest(GET_CURRENT_SCHOOL_CALENDAR, {districtId, schoolId});

  if (!response.getCurrentSchoolYearCalendar) {
    return null;
  }

  return {
    startDate: response.getCurrentSchoolYearCalendar.startDate ? new Date(response.getCurrentSchoolYearCalendar.startDate) : null,
    endDate: response.getCurrentSchoolYearCalendar.endDate ? new Date(response.getCurrentSchoolYearCalendar.endDate) : null
  };
}

/**
 * Gets assessment calendar information for a school
 *
 * @param {string} districtId - The ID of the district
 * @param {string} schoolId - The ID of the school
 * @returns {Promise<Object>} - Object containing assessment calendar data with schoolYear
 */
async function getAssessmentCalendar(districtId, schoolId) {
  const response = await graphqlRequest(GET_ASSESSMENT_CALENDAR, {schoolId, districtId});

  if (!response.assessmentCalendar) {
    return null;
  }

  return {
    schoolId: response.assessmentCalendar.schoolId,
    schoolYear: response.assessmentCalendar.schoolYear,
    startDate: response.assessmentCalendar.startDate ? new Date(response.assessmentCalendar.startDate) : null,
    periods: response.assessmentCalendar.periods || []
  };
}

/**
 * Gets calendar information with retries
 * 
 * @param {string} schoolId - The ID of the school
 * @param {number} retryCount - Current retry count (internal use)
 * @returns {Promise<Object>} - Object containing schoolYear and periodIdentifier
 */
async function getCurrentPeriodWithRetries(schoolId, retryCount = 0) {
  const inSchoolId = `${schoolId}`;
  try {
    const result = await getCurrentPeriod(inSchoolId);
    
    // Handle null response
    if (!result) {
      if (retryCount < 3) {
        logSessionInfo("calendar_service_retry", {
          retryCount: retryCount + 1,
          inSchoolId,
          reason: "null_response"
        });
        return await getCurrentPeriodWithRetries(inSchoolId, retryCount + 1);
      }
      throw new Error("Failed to get calendar info - null response after retries");
    }
    
    return result;
  } catch (error) {
    // Handle errors with retry
    if (retryCount < 3) {
      logSessionInfo("calendar_service_retry", {
        retryCount: retryCount + 1,
        inSchoolId,
        reason: "request_error",
        error: error.message
      });
      return await getCurrentPeriodWithRetries(inSchoolId, retryCount + 1);
    }
    throw error;
  }
}

/**
 * Gets school year calendar information with retries
 * 
 * @param {string} districtId - The ID of the district
 * @param {string} schoolId - The ID of the school
 * @param {number} retryCount - Current retry count (internal use)
 * @returns {Promise<Object>} - Object containing startDate and endDate
 */
async function getCurrentSchoolYearCalendarWithRetries(districtId, schoolId, retryCount = 0) {
  const inDistrictId = `${districtId}`;
  const inSchoolId = `${schoolId}`;

  let result;
  try {
    result = await getCurrentSchoolYearCalendar(inDistrictId, inSchoolId);
  } catch (error) {
    if (retryCount < 3) {
      logSessionInfo("calendar_service_retry", {
        retryCount: retryCount + 1,
        inDistrictId,
        inSchoolId,
        reason: "request_error",
        error: error.message
      });
      return getCurrentSchoolYearCalendarWithRetries(inDistrictId, inSchoolId, retryCount + 1);
    }
    throw error;
  }

  if (!result) {
    if (retryCount < 3) {
      logSessionInfo("calendar_service_retry", {
        retryCount: retryCount + 1,
        inDistrictId,
        inSchoolId,
        reason: "null_response"
      });
      return getCurrentSchoolYearCalendarWithRetries(inDistrictId, inSchoolId, retryCount + 1);
    }
    throw new Error("Failed to get school year calendar info - null response after retries");
  }

  return result;
}

/**
 * Gets assessment calendar information with retries
 *
 * @param {string} districtId - The ID of the district
 * @param {string} schoolId - The ID of the school
 * @param {number} retryCount - Current retry count (internal use)
 * @returns {Promise<Object>} - Object containing assessment calendar data with schoolYear
 */
async function getAssessmentCalendarWithRetries(districtId, schoolId, retryCount = 0) {
  const inDistrictId = `${districtId}`;
  const inSchoolId = `${schoolId}`;

  let result;
  try {
    result = await getAssessmentCalendar(inDistrictId, inSchoolId);
  } catch (error) {
    if (retryCount < 3) {
      logSessionInfo("calendar_service_retry", {
        retryCount: retryCount + 1,
        inDistrictId,
        inSchoolId,
        reason: "request_error",
        error: error.message
      });
      return getAssessmentCalendarWithRetries(inDistrictId, inSchoolId, retryCount + 1);
    }
    throw error;
  }

  if (!result || result.schoolYear == null) {
    if (retryCount < 3) {
      logSessionInfo("calendar_service_retry", {
        retryCount: retryCount + 1,
        inDistrictId,
        inSchoolId,
        reason: result ? "missing_school_year" : "null_response"
      });
      return getAssessmentCalendarWithRetries(inDistrictId, inSchoolId, retryCount + 1);
    }
    throw new Error("Failed to get assessment calendar info - schoolYear missing after retries");
  }

  return result;
}

export default {
  getCurrentPeriod: getCurrentPeriodWithRetries,
  getCurrentSchoolYearCalendar: getCurrentSchoolYearCalendarWithRetries,
  getAssessmentCalendar: getAssessmentCalendarWithRetries
}
